import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  Input,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
  input,
  signal,
  viewChild,
} from '@angular/core';
import { ControlValueAccessor, FormControl } from '@angular/forms';
import { isEqual } from 'lodash-es';
import { Subject, distinctUntilChanged, map, takeUntil, tap } from 'rxjs';

import {
  FormControlComponent,
  setControlRelationship,
} from '@plugins/client/forms/form';
import type {
  Maybe,
  Nullish,
  SingleOrArray,
} from '@plugins/shared/types/helpers';
import { isNullish } from '@plugins/shared/utils/assertFunctions';

import type {
  BaseCVAHandler,
  SearchTypes,
} from '../../private/utils/types.private';
import { PickedEntitiesService } from '../services';
import { UserSearchComponent } from '../user-search/user-search.component';

type OnChange = (arg: unknown) => void;

/** Manage picked entities by searching, adding, or removing them via a search box
 * and a picked list.
 * Requires a `FormControl<string[]>` in order to render.
 *
 * Example:
 * ```html
 <plugins-user-search-and-pick
  formControlName="controlName"
  searchType="user"
  searchFieldPlaceholderText="Your search field placeholder"
  pickedEntitiesPlaceholder="No users selected"
  [excludeFromSearch]="['excludedUserId1', 'excludedUserId2']"
  [maxAllowed]="'1'"
  />
 * ```
 *  */
@Component({
  selector: 'plugins-user-search-and-pick',
  standalone: false,
  templateUrl: './user-search-and-pick.component.html',
  styleUrl: './user-search-and-pick.component.scss',
  providers: [PickedEntitiesService],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UserSearchAndPickComponent
  extends FormControlComponent
  implements OnInit, OnDestroy, ControlValueAccessor
{
  readonly searchRef = viewChild<UserSearchComponent>(UserSearchComponent);

  readonly searchFieldPlaceholderText = input<string | Nullish>();
  /** Placeholder (text or `TemplateRef`) to show when no entities have been selected yet */
  readonly pickedEntitiesPlaceholder =
    input<Maybe<string | TemplateRef<unknown>>>();
  /** Can be used for fetch optimization or narrowing when making calls to the `UserDetailsService`.
   * Defaults to `all`. */
  readonly searchType = input<SearchTypes>('all');
  /** List of ids that should not used in requests sent to the user API */
  // Keepint Input decorator until rxjs-interop is stable
  // eslint-disable-next-line @angular-eslint/prefer-signals
  @Input() set excludeFromSearch(excluded: Maybe<SingleOrArray<string>>) {
    this.pickedService.excludeIdsFromSearch(excluded ?? []);
  }

  /** Limit responses of the user API to members of specified groups.
   * This has no effect if the `searchType` is set to `group` */
  readonly includedGroups = input<SingleOrArray<string>>([]);
  /** Maximum allowed entity picks.
   * If limit is reached or exceeded, the component is disabled. */
  readonly maxAllowed = input<number | string | Nullish>();
  /** Controls the size of the search results and picked entities. */
  readonly size = input<number | string | Nullish>();
  /** Should entieis have a detailed title associated with them? */
  readonly detailedTitle = input<boolean | Nullish>();
  /** Outline color for the `UserSearchComponent` input and available options on
   * keyboard navigation */
  readonly color = input('');
  /** Should picked entities link to user profile page. Defaults to `true` */
  readonly profileLinksEnabled = input(true);

  private pickedService = inject(PickedEntitiesService);
  private changeDetectorRef = inject(ChangeDetectorRef);

  searchControl = new FormControl<string>('');
  readonly disablePicked = signal<boolean>(false);

  pickedLength$ = this.pickedService.pickedIds$.pipe(
    map((picked) => picked.length),
  );

  destroy$ = new Subject<void>();

  // ControlValueAccessor properties
  private onChange!: OnChange;
  private onTouched!: BaseCVAHandler;

  constructor() {
    super();
    if (this.controlDirective) {
      this.controlDirective.valueAccessor = this;
    }
  }

  private setSearchControl(): void {
    if (!this.controlDirective) return;
    setControlRelationship({
      controlDirective: this.controlDirective,
      formControl: this.searchControl,
      onChange: this.onChange,
      destroy$: this.destroy$,
      changeDetectorRef: this.changeDetectorRef,
      emitEvent: true,
    });
  }

  private setControlDirective(): void {
    if (isNullish(this.controlDirective?.valueChanges)) return;
    // Bind form control value to picked service
    this.pickedService.setIds(this.controlDirective.value as string[]);

    this.controlDirective.valueChanges
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap((change: string[]) => {
          if (!isEqual(change, this.pickedService.pickedIds)) {
            this.pickedService.setIds(change);
          }
        }),
        takeUntil(this.destroy$),
      )
      .subscribe();

    this.pickedService.pickedIds$
      .pipe(
        distinctUntilChanged((prev, curr) => isEqual(prev, curr)),
        tap((ids) => {
          if (!isEqual(this.controlDirective?.value, ids)) {
            this.controlDirective?.control?.patchValue(ids);
            this.controlDirective?.control?.markAsDirty();
          }
        }),

        takeUntil(this.destroy$),
      )
      .subscribe();
  }

  override focus() {
    return this.searchRef()?.focus();
  }

  // ControlValueAccessor methods
  writeValue(val: string[]): void {
    return;
  }
  registerOnChange(fn: OnChange): void {
    this.onChange = fn;
  }
  registerOnTouched(fn: BaseCVAHandler): void {
    this.onTouched = fn;
  }

  setDisabledState(disabled: boolean): void {
    if (disabled) {
      this.searchControl.disable({ emitEvent: false });
      this.disablePicked.set(true);
      return;
    }
    this.searchControl.enable({ emitEvent: false });
    this.disablePicked.set(false);
  }

  override ngOnInit(): void {
    super.ngOnInit();
    this.setSearchControl();
    this.setControlDirective();
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
    this.destroy$.next();
    this.destroy$.complete();
  }
}
